// web/src/pages/workbench/context/snapshot.ts —— 语境桶的会话级快照（sessionStorage 读写）
//
// 只做「浏览器存储 + 裁剪」，不认识 Bucket / ASSIST_INIT 这些 store 运行时对象：
// store.ts 会 import 本模块，这里反过来再引它的运行时值就成环，故只收/只出扁平的落盘形态。
//
// 落盘口径（见 store.ts 文件头）——**只留刷新不该丢、且服务端重建不出来的那一点**：
// - 在线会话指针 chatId（决定刷新后回到哪个会话）+ 每个 chat 桶的最新一条意图/情绪；
// - 语音桶不落（浏览器刷新销毁 WebRTC，这一通已断，留内容只会显示不存在的「实时状态」）；
// - 质检告警不落（monitor 侧回填可重建，双源还会重复计数）；对话内容不落（子页会话自带）。
//
// 归属：按登录用户 id 存，换账号即作废（共享终端上不把上一位坐席的内容留给下一位）。

import { useAuthStore } from '../../../store/auth';

/** 快照存储键（「清理工作台」与换账号都要清它） */
export const SNAPSHOT_KEY = 'bcc-wb-ctx';

/** 一条落盘桶：只有指针与两条胶囊值，其余切片一律不落 */
export interface SnapshotBucket {
  key: string;
  kind: string;
  ref: string;
  createdAt: number;
  updatedAt: number;
  /** 工具条意图胶囊的最新值（IntentInfo；这里只做透明搬运） */
  intent: unknown;
  /** 工具条情绪胶囊的最新值（EmotionInfo） */
  emotion: unknown;
}

export interface SnapshotData {
  chatId: number | null;
  buckets: SnapshotBucket[];
}

interface Stored {
  userId?: number;
  chatId?: number | null;
  buckets?: SnapshotBucket[];
}

/** 当前登录用户 id（未登录为 0） */
function authUserId(): number {
  return Number(useAuthStore.getState().user?.id) || 0;
}

/**
 * 读快照：解析失败 / 隐私模式 / 换账号 / 旧格式一律当空快照（退回纯内存态）。
 * `keepMs` 由调用方传（与桶的保留窗同一常量，避免两处各写一个数）。
 */
export function readSnapshot(keepMs: number): SnapshotData {
  try {
    const raw = sessionStorage.getItem(SNAPSHOT_KEY);
    if (!raw) return { chatId: null, buckets: [] };
    const data = JSON.parse(raw) as Stored;
    if (!data.userId || data.userId !== authUserId()) return { chatId: null, buckets: [] };
    const now = Date.now();
    const buckets = (Array.isArray(data.buckets) ? data.buckets : []).filter(
      (b) => b && typeof b.key === 'string' && b.key.startsWith('chat:') && now - (Number(b.updatedAt) || 0) <= keepMs
    );
    return { chatId: typeof data.chatId === 'number' ? data.chatId : null, buckets };
  } catch {
    return { chatId: null, buckets: [] };
  }
}

/** 写快照（调用方已完成裁剪与排序：本模块不判断该留几个桶） */
export function writeSnapshot(data: SnapshotData): void {
  try {
    sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ userId: authUserId(), chatId: data.chatId, buckets: data.buckets }));
  } catch {
    /* 隐私模式 / 配额超限：放弃落盘，退回纯内存态 */
  }
}

/** 丢弃快照（换账号 / 「清理工作台」） */
export function clearSnapshot(): void {
  try {
    sessionStorage.removeItem(SNAPSHOT_KEY);
  } catch {
    /* 存储不可用：忽略 */
  }
}
